import Service, { type Registry as Services, service } from '@ember/service';
import type { SeenUserEngagementElement } from 'lumen/models';
import type { UserEngagementElementId } from 'lumen/models/seen-user-engagement-element';

export default class UserEngagementService extends Service {
  @service declare errorTracking: Services['error-tracking'];
  @service declare store: Services['store'];

  get seenUserEngagementElements(): SeenUserEngagementElement[] {
    return [
      ...this.store.peekAll<SeenUserEngagementElement>(
        'seen-user-engagement-element',
      ),
    ];
  }

  async getSeenUserEngagementElements(): Promise<void> {
    // Catch any errors, because this will be called on page load and we don't want to
    // crash the app under any circumstances. There could be errors with parsing the JSON string.
    // In the worst case, we'll show user engagement elements again to users who have already seen them.
    try {
      await this.store.query<SeenUserEngagementElement>(
        'seen-user-engagement-element',
        {
          namespace: 'api/v3',
          typeKey: 'user_engagements_elements',
        },
      );
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  }

  hasSeen(userEngagementElementId: UserEngagementElementId): boolean {
    return Boolean(
      this.seenUserEngagementElements.find(
        (element) => element.elementId === userEngagementElementId,
      ),
    );
  }

  markAsSeen(userEngagementElementId: UserEngagementElementId): void {
    if (this.hasSeen(userEngagementElementId)) {
      return;
    }

    try {
      const seenUserEngagementElementRecord =
        this.store.createRecord<SeenUserEngagementElement>(
          'seen-user-engagement-element',
          {
            id: userEngagementElementId,
            elementId: userEngagementElementId,
          },
        );

      void seenUserEngagementElementRecord.save();
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'user-engagement': UserEngagementService;
  }
}
